import i18n from '@/plugins/i18n';

/**
 * Discount Types
 * Currency value should be overwritten based on locale
 */
export const DISCOUNTS = Object.freeze({
  CURRENCY: {
    name: 'currency',
    value: '$',
  },
  PERCENT: {
    name: 'percent',
    value: '%',
  },
});

/**
 * Credit Reasons
 */
export const CREDIT_REASONS = Object.freeze({
  PROMO_NEW_CUSTOMER: {
    id: 'promo_new_customer',
    name: i18n.t('constants.credit_reasons.promo_new_customer'),
  },
  PROMO_EXISTING_CUSTOMER: {
    id: 'promo_existing_customer',
    name: i18n.t('constants.credit_reasons.promo_existing_customer'),
  },
  RETURN_DEFECTIVE: {
    id: 'return_defective',
    name: i18n.t('constants.credit_reasons.return_defective'),
  },
  RETURN_EXPIRED: {
    id: 'return_expired',
    name: i18n.t('constants.credit_reasons.return_expired'),
  },
  RETURN_PACKAGING: {
    id: 'return_packaging',
    name: i18n.t('constants.credit_reasons.return_packaging'),
  },
  RETURN_ORDER_ISSUE: {
    id: 'return_order_issue',
    name: i18n.t('constants.credit_reasons.return_order_issue'),
  },
  RETURN_OTHER: {
    id: 'return_other',
    name: i18n.t('constants.credit_reasons.return_other'),
  },
  OTHER: {
    id: 'other',
    name: i18n.t('constants.credit_reasons.other'),
  },
});

/**
 * Order Create/Edit/Split "Sort By" Options
 */
export const ORDER_CREATE_SORT_BY = Object.freeze({
  MANUAL: {
    id: 'manual',
    name: i18n.t('constants.createOrderSortBy.manual'),
  },
  PRODUCT_NAME_ASC: {
    id: 'productNameAsc',
    name: i18n.t('constants.createOrderSortBy.productNameAsc'),
  },
  PRODUCT_LINE_ASC: {
    id: 'productLineAsc',
    name: i18n.t('constants.createOrderSortBy.productLineAsc'),
  },
  SKU_ASC: {
    id: 'skuAsc',
    name: i18n.t('constants.createOrderSortBy.skuAsc'),
  },
});

/**
 * Product Return Reasons
 */
export const RETURN_REASONS = Object.freeze({
  EXPIRED: {
    id: 'expired',
    name: i18n.t('constants.returnReasons.expired'),
  },
  DEFECTIVE: {
    id: 'defective',
    name: i18n.t('constants.returnReasons.defective'),
  },
  PACKAGING_ISSUE: {
    id: 'packaging_issue',
    name: i18n.t('constants.returnReasons.packagingIssue'),
  },
  REJECTED: {
    id: 'rejected',
    name: i18n.t('constants.returnReasons.rejected'),
  },
  ORDER_ISSUE: {
    id: 'order_issue',
    name: i18n.t('constants.returnReasons.orderIssue'),
  },
  OTHER: {
    id: 'other',
    name: i18n.t('constants.returnReasons.other'),
  },
});

/**
 * Inbound Leads types
 * Needs to match the CompanyRequestType choices from the backend: apps.notifications.choices.CompanyRequestType
 */
export const INBOUND_LEADS_TYPES = Object.freeze({
  GENERAL: {
    id: 0,
    name: i18n.t('constants.inboundLeadsTypes.general'),
  },
  SAMPLE_REQUEST: {
    id: 1,
    name: i18n.t('constants.inboundLeadsTypes.sampleRequest'),
  },
  PRODUCT_INQUIRY: {
    id: 2,
    name: i18n.t('constants.inboundLeadsTypes.productInquiry'),
  },
  INFORMATION_REQUEST: {
    id: 3,
    name: i18n.t('constants.inboundLeadsTypes.informationRequest'),
  },
  NOTIFY_ME: {
    id: 4,
    name: i18n.t('constants.inboundLeadsTypes.notifyMe'),
  },
});

export const SELECT_OPTIONS = Object.freeze({
  ALL: {
    id: 1,
    name: i18n.t('constants.checkboxStatuses.all'),
  },
  SELECTED: {
    id: 2,
    name: i18n.t('constants.checkboxStatuses.selected'),
  },
  UNSELECTED: {
    id: 3,
    name: i18n.t('constants.checkboxStatuses.unselected'),
  },
});

/**
 * Order payment types
 */
export const ORDER_PAYMENT_TYPES = Object.freeze({
  FULL: {
    id: 'full',
    name: i18n.t('constants.orderPaymentTypes.fullPayment'),
  },
  PARTIAL: {
    id: 'partial',
    name: i18n.t('constants.orderPaymentTypes.partialPayment'),
  },
});

/**
 * Payment methods
 */
export const PAYMENT_METHODS = Object.freeze({
  CASH: {
    id: 'cash',
    name: i18n.t('constants.paymentMethods.cash'),
  },
  CHECK: {
    id: 'check',
    name: i18n.t('constants.paymentMethods.check'),
  },
  CREDIT: {
    id: 'credit',
    name: i18n.t('constants.paymentMethods.credit'),
  },
  TRADE: {
    id: 'trade',
    name: i18n.t('constants.paymentMethods.trade'),
  },
  ACH: {
    id: 'ach',
    name: i18n.t('constants.paymentMethods.ach'),
  },
  WIRE: {
    id: 'wire',
    name: i18n.t('constants.paymentMethods.wireTransfer'),
  },
  cashier: {
    id: 'cashier',
    name: i18n.t('constants.paymentMethods.cashiersCheck'),
  },
  LEAFLINK_FINANCIAL: {
    id: 'leaflink_financial',
    name: i18n.t('constants.paymentMethods.leaflinkFinancial'),
  },
  OTHER: {
    id: 'other',
    name: i18n.t('constants.paymentMethods.other'),
  },
});

/**
 * Order classicification at the time of creation
 */
export const LICENSE_CLASSIFICATIONS = Object.freeze({
  NONE: {
    id: 'None',
    name: i18n.t('constants.classifications.none'),
    displayName: '',
  },
  ADULT_USE: {
    id: 'Adult Use',
    name: i18n.t('constants.classifications.adultUse'),
    displayName: 'Recreational',
  },
  MEDICAL: {
    id: 'Medical',
    name: i18n.t('constants.classifications.medical'),
    displayName: 'Medical',
  },
  COMBINED: {
    id: 'Combined',
    name: i18n.t('constants.classifications.combined'),
    displayName: 'Recreational',
  },
  CANNABIS: {
    id: 'Cannabis',
    name: i18n.t('constants.classifications.cannabis'),
    displayName: 'Recreational',
  },
});

/**
 * Additional license classifications
 */
export const ADDITIONAL_LICENSE_CLASSIFICATIONS = Object.freeze({
  ANCILLARY: {
    id: 'Ancillary',
    name: i18n.t('constants.classifications.ancillary'),
    displayName: 'ancillary',
  },
});

/**
 * Additional license types
 */
export const LICENSE_TYPES = Object.freeze({
  BULK: 'BULK',
});

export const TIME_PERIODS = Object.freeze({
  HOUR: {
    id: 'hours',
    value: 'hour',
    name: i18n.t('constants.timePeriods.hour'),
  },
  DAY: {
    id: 'days',
    value: 'day',
    name: i18n.t('constants.timePeriods.day'),
  },
  WEEK: {
    id: 'weeks',
    value: 'week',
    name: i18n.t('constants.timePeriods.week'),
  },
});

export const WEEKDAYS = Object.freeze({
  SUNDAY: {
    id: 6,
    name: i18n.t('ll.date.weekdays.sunday'),
  },
  MONDAY: {
    id: 0,
    name: i18n.t('ll.date.weekdays.monday'),
  },
  TUESDAY: {
    id: 1,
    name: i18n.t('ll.date.weekdays.tuesday'),
  },
  WEDNESDAY: {
    id: 2,
    name: i18n.t('ll.date.weekdays.wednesday'),
  },
  THURSDAY: {
    id: 3,
    name: i18n.t('ll.date.weekdays.thursday'),
  },
  FRIDAY: {
    id: 4,
    name: i18n.t('ll.date.weekdays.friday'),
  },
  SATURDAY: {
    id: 5,
    name: i18n.t('ll.date.weekdays.saturday'),
  },
});

export const TWELVE_HOUR_TIME_PERIODS = Object.freeze({
  AM: {
    id: 'am',
    name: 'AM',
  },
  PM: {
    id: 'pm',
    name: 'PM',
  },
});

export const NOTIFICATION_PREFERENCE_TYPES = Object.freeze({
  EMAIL: {
    id: 'Email',
    name: i18n.t('common.email'),
  },
  SMS: {
    id: 'Sms',
    name: i18n.t('common.text'),
  },
});

export const ON_CALL_FOR_TYPES = Object.freeze({
  BOTH: {
    text: i18n.t('common.both'),
    id: 0,
    active: false,
  },
  DELIVERY: {
    text: i18n.t('common.delivery'),
    id: 2,
    active: false,
  },
  PICKUP: {
    text: i18n.t('common.pickup'),
    id: 1,
    active: false,
  },
});

export const BUSINESS_DAYS = Object.freeze({
  MONDAY: {
    id: 0,
    name: i18n.t('ll.date.weekdays.monday'),
  },
  TUESDAY: {
    id: 1,
    name: i18n.t('ll.date.weekdays.tuesday'),
  },
  WEDNESDAY: {
    id: 2,
    name: i18n.t('ll.date.weekdays.wednesday'),
  },
  THURSDAY: {
    id: 3,
    name: i18n.t('ll.date.weekdays.thursday'),
  },
  FRIDAY: {
    id: 4,
    name: i18n.t('ll.date.weekdays.friday'),
  },
});

/**
 * Legacy notification types
 * @deprecated
 */
export const SHIPPING_NOTIFICATION_TYPES = Object.freeze({
  EMAIL: {
    id: 'email',
    name: i18n.t('common.email'),
  },
  TEXT: {
    id: 'text',
    name: i18n.t('common.text'),
  },
});

/**
 * Volume Discount Types
 */
export const VOLUME_DISCOUNTS = Object.freeze({
  PERCENT: {
    id: 'percent',
    name: '%',
  },
});

/**
 * Threshold types for Volume Discount
 */
export const THRESHOLD_TYPES = Object.freeze({
  TTL: 'ttl',
  QTY: 'qty',
});

export const LISTING_ALLOCATION_TYPE = Object.freeze({
  BUYER: 'BUYER',
  GROUP: 'GROUP',
});
